
import AddFilesToProjectModal from '@/components/projects/summary/AddFilesToProjectModal.vue';
import FileExaminationSummaryTable from '@/components/projects/summary/ProjectSummaryFilesTable.vue';
import ProjectDetailsSummary from '@/components/projects/summary/ProjectDetailsSummary.vue';
import ProjectFilesSummary from '@/components/projects/summary/ProjectFilesSummary.vue';
import amplitudeService from '@/services/AmplitudeService';
import projectService from '@/services/ProjectService';
import UsesResultsStore from '@/components/hoc/UsesResultsStore';
import { Component, Watch } from 'vue-property-decorator';
import { Get } from 'vuex-pathify';
import { Route } from 'vue-router';
import { IProjectFile, ProjectStatus } from '@/types/resource.types';

@Component({
    name: 'ProjectSummaryView',
    components: {
        AddFilesToProjectModal,
        FileExaminationSummaryTable,
        ProjectDetailsSummary,
        ProjectFilesSummary
    }
})
export default class ProjectSummaryView extends UsesResultsStore {

    @Get('results/projectsListPath')
    readonly projectsListPath: string | null;

    loading: boolean = false;
    projectId: number = 0;
    private hasTrackedProjectOpen: boolean = false;
    timer: ReturnType<typeof setInterval> | null = null;
    /* Methods
    ============================================*/

    async confirmCancel(): Promise<boolean | undefined> {
        return await this.$confirm(`
            Are you sure you want to delete this project? Any associated files will also be deleted.
            This action cannot be undone.`
        ,{
            icon: 'mdi-alert',
            title: 'Delete Project?'
        });
    }
    
    async cancelProject() {
        try {
            if(!(await this.confirmCancel())) return;
            this.loading = true;
            await projectService.delete(this.projectId);
            this.$router.push(this.$paths.PROJECTS);
        } catch(e) {
            this.$logger.logError(e);
        } finally {
            this.loading = false;
        }
    }

    async handleUploadComplete(_files: IProjectFile[]) {
        await this.loadProjectSummary(this.projectId);
        this.startSummaryRefreshTimer();
    }

    startSummaryRefreshTimer() {
        if(this.timer) return;
        this.timer = setInterval(() => {
            this.loadProjectSummary(this.projectId);
        }, 60000);
    }

    stopSummaryRefreshTimer() {
        if(!this.timer) return;
        clearInterval(this.timer);
        this.timer = null;
    }

    get isComplete(){
        if(this.projectSummary?.project?.status == null) return false;
        return this.projectSummary?.project?.status == ProjectStatus.Complete
        || this.projectSummary?.project?.status == ProjectStatus.Cancelled
        || this.projectSummary?.project?.status == ProjectStatus.Error;
    }
    /* Lifecyce Hooks
    ============================================*/

    beforeDestroy(){
        this.stopSummaryRefreshTimer();
    }
    beforeMount() {
        let projectId = parseInt(this.$route.params?.id);
        if(!projectId) return;
        this.projectId = projectId;
        this.loadProject(projectId);
        this.startSummaryRefreshTimer();
    }

    /* Watchers
    ============================================*/

    @Watch('$route')
    onRouteChanged($routeNew: Route) {
        let id = parseInt($routeNew?.params?.id);
        if(id === this.project?.id) return;
        this.loadProject(id);
    }
    @Watch('projectSummary')
    onProjectChanged() {
        if (!this.hasTrackedProjectOpen && this.projectSummary?.project) {
            this.hasTrackedProjectOpen = true;
            amplitudeService.trackProjectOpened({
                projectId: this.projectId,
                numberFilesInProject: this.projectSummary.project.files?.length ?? 0,
                userTenancyAccount: (this as any).$store?.state?.subscriber?.id,
                openedByUser: (this as any).$user?.email
            });
        }
        if(this.isComplete){
            this.stopSummaryRefreshTimer();
            return;
        }
        this.startSummaryRefreshTimer();
    }

}

